import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { AppSettings, SettingsResponse } from '../settings/types';
import { loadSettings, saveSettings, subscribeSettings } from '../settings/settingsStorage';
import { apiFetch } from '../services/apiClient';
import { useAuth } from './AuthContext';

type SettingsContextValue = {
  settings: AppSettings;
  configuredSecrets: string[];
  secretsStorage: 'secret-manager' | 'local';
  isSecretConfigured: (path: string) => boolean;
  updateSettings: (patch: Partial<AppSettings> | ((prev: AppSettings) => AppSettings)) => void;
  replaceIntegrations: (
    key: keyof AppSettings['integrations'],
    value: AppSettings['integrations'][typeof key]
  ) => Promise<void>;
  ready: boolean;
};

const Ctx = createContext<SettingsContextValue | null>(null);

function parseSettingsResponse(remote: SettingsResponse): {
  settings: AppSettings;
  configuredSecrets: string[];
  secretsStorage: 'secret-manager' | 'local';
} {
  const { configuredSecrets, secretsStorage, ...settings } = remote;
  return { settings, configuredSecrets: configuredSecrets || [], secretsStorage: secretsStorage || 'local' };
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const { enabled: backend, user } = useAuth();
  const [settings, setSettings] = useState<AppSettings>(() => loadSettings());
  const [configuredSecrets, setConfiguredSecrets] = useState<string[]>([]);
  const [secretsStorage, setSecretsStorage] = useState<'secret-manager' | 'local'>('local');
  const [ready, setReady] = useState(!backend || !user);

  useEffect(() => {
    if (!backend || !user) {
      setReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const remote = await apiFetch<SettingsResponse>('/api/settings');
        if (!cancelled) {
          const parsed = parseSettingsResponse(remote);
          setSettings(parsed.settings);
          setConfiguredSecrets(parsed.configuredSecrets);
          setSecretsStorage(parsed.secretsStorage);
        }
      } catch {
        if (!cancelled) {
          setSettings(loadSettings());
          setConfiguredSecrets([]);
          setSecretsStorage('local');
        }
      } finally {
        if (!cancelled) {
          setReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [backend, user?.id]);

  useEffect(() => {
    if (backend && user) {
      return;
    }
    return subscribeSettings(() => setSettings(loadSettings()));
  }, [backend, user]);

  const persist = useCallback(
    async (next: AppSettings) => {
      if (backend && user) {
        const remote = await apiFetch<SettingsResponse>('/api/settings', {
          method: 'PUT',
          body: JSON.stringify(next),
        });
        const parsed = parseSettingsResponse(remote);
        setSettings(parsed.settings);
        setConfiguredSecrets(parsed.configuredSecrets);
        setSecretsStorage(parsed.secretsStorage);
        return parsed.settings;
      }
      saveSettings(next);
      return next;
    },
    [backend, user]
  );

  const updateSettings = useCallback(
    (patch: Partial<AppSettings> | ((prev: AppSettings) => AppSettings)) => {
      setSettings((prev) => {
        const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
        void persist(next);
        return next;
      });
    },
    [persist]
  );

  const replaceIntegrations = useCallback(
    async (key: keyof AppSettings['integrations'], value: AppSettings['integrations'][typeof key]) => {
      const next: AppSettings = {
        ...settings,
        version: 2,
        integrations: { ...settings.integrations, [key]: value },
      };
      await persist(next);
    },
    [persist, settings]
  );

  const isSecretConfigured = useCallback(
    (path: string) => backend && configuredSecrets.includes(path),
    [backend, configuredSecrets]
  );

  const value = useMemo(
    () => ({
      settings,
      configuredSecrets,
      secretsStorage,
      isSecretConfigured,
      updateSettings,
      replaceIntegrations,
      ready,
    }),
    [settings, configuredSecrets, secretsStorage, isSecretConfigured, updateSettings, replaceIntegrations, ready]
  );

  if (!ready) {
    return null;
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAppSettings() {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error('useAppSettings must be used within SettingsProvider');
  }
  return v;
}
